import { useEffect } from 'react';
import { useStoryStore } from '../store/storyStore';
import type { ReactCallbacks } from '../types/interop.types';

/**
 * Hook to establish communication between Blazor and React
 * Registers callbacks that Blazor can invoke
 */
export function useBlazorInterop() {
  const { setAvailableScenes, setScene, setNarrative, setChoices, setGameState, setStoryCompleted, setDonationSettings, playCredits } = useStoryStore();

  useEffect(() => {
    console.log('[useBlazorInterop] Registering callbacks with Blazor');

    // Check if interop is available
    if (!window.NordicNeveInterop) {
      console.error('[useBlazorInterop] NordicNeveInterop not found on window!');
      return;
    }

    // Define callbacks that Blazor will invoke
    const callbacks: ReactCallbacks = {
      updateAvailableScenes: (scenes) => {
        console.log('[useBlazorInterop] Received available scenes:', scenes.length);
        setAvailableScenes(scenes);
      },

      updateScene: (sceneData) => {
        console.log('[useBlazorInterop] Received scene update:', sceneData);
        setScene(sceneData);
      },

      updateNarrative: (narrativeData) => {
        console.log('[useBlazorInterop] Received narrative update:', narrativeData);
        setNarrative(narrativeData);
      },

      updateChoices: (choices) => {
        console.log('[useBlazorInterop] Received choices update:', choices.length);
        setChoices(choices);
      },

      updateGameState: (gameState) => {
        console.log('[useBlazorInterop] Received game state update:', gameState);
        setGameState(gameState);
      },

      notifyStoryCompleted: () => {
        console.log('[useBlazorInterop] Story completed');
        setStoryCompleted(true);
      },

      displayCredits: (creditsData) => {
        console.log('[useBlazorInterop] Received credits:', creditsData);
        playCredits(creditsData);
      },

      updateDonationSettings: (showDonation, donationUsername) => {
        console.log('[useBlazorInterop] Received donation settings:', { showDonation, donationUsername });
        setDonationSettings(showDonation, donationUsername);
      },
    };

    // Register callbacks with Blazor
    window.NordicNeveInterop.registerReactCallbacks(callbacks);

    // Notify Blazor that React is ready
    if (window.NordicNeveInterop.notifyReactReady) {
        window.NordicNeveInterop.notifyReactReady();
    }

    console.log('[useBlazorInterop] Callbacks registered successfully');

    // Cleanup on unmount
    return () => {
      console.log('[useBlazorInterop] Cleaning up');
    };
  }, [setAvailableScenes, setScene, setNarrative, setChoices, setGameState, setStoryCompleted, setDonationSettings]);
}
